import React from "react";
import {
  Dialog,
  DialogTitle,
  DialogContent,
  DialogActions,
  Button,
  List,
  ListItem,
  ListItemButton,
  ListItemText,
  Box,
  Typography,
  IconButton,
  CircularProgress,
  Alert,
} from "@mui/material";
import CloseIcon from "@mui/icons-material/Close";
import VpnKeyIcon from "@mui/icons-material/VpnKey";
import { useConnections } from "../hooks/useConnections";

interface Props {
  open: boolean;
  selectedName: string | null;
  onClose: () => void;
  onSelect: (name: string) => void;
}

export const ConnectionsDialog: React.FC<Props> = ({ open, selectedName, onClose, onSelect }) => {
  const { names, loading, error } = useConnections(open);

  return (
    <Dialog
      open={open}
      onClose={onClose}
      maxWidth="xs"
      fullWidth
      PaperProps={{ sx: { borderRadius: 2 } }}
    >
      <DialogTitle sx={{ pb: 1 }}>
        <Box sx={{ display: "flex", alignItems: "center", justifyContent: "space-between" }}>
          <Box sx={{ display: "flex", alignItems: "center", gap: 1 }}>
            <VpnKeyIcon fontSize="small" color="primary" />
            <Typography variant="h6" sx={{ fontWeight: 600 }}>
              Saved Connections
            </Typography>
          </Box>
          <IconButton onClick={onClose} size="small">
            <CloseIcon />
          </IconButton>
        </Box>
      </DialogTitle>

      <DialogContent dividers sx={{ p: 0 }}>
        {loading ? (
          <Box sx={{ display: "flex", justifyContent: "center", py: 4 }}>
            <CircularProgress size={28} />
          </Box>
        ) : error ? (
          <Box sx={{ p: 2 }}>
            <Alert severity="error">{error}</Alert>
          </Box>
        ) : names.length === 0 ? (
          <Box sx={{ p: 3, textAlign: "center" }}>
            <Typography variant="body2" color="text.secondary">
              No saved connections yet.
            </Typography>
          </Box>
        ) : (
          <List disablePadding>
            {names.map((name) => {
              const isSelected = selectedName === name;
              return (
                <ListItem key={name} disablePadding>
                  <ListItemButton
                    selected={isSelected}
                    onClick={() => onSelect(name)}
                    sx={{
                      "&.Mui-selected": {
                        backgroundColor: "primary.main",
                        color: "white",
                        "&:hover": { backgroundColor: "primary.dark" },
                      },
                    }}
                  >
                    <ListItemText
                      primary={name}
                      secondary={isSelected ? "Currently selected" : undefined}
                      secondaryTypographyProps={{
                        sx: { color: isSelected ? "rgba(255,255,255,0.8)" : undefined },
                      }}
                    />
                  </ListItemButton>
                </ListItem>
              );
            })}
          </List>
        )}
      </DialogContent>

      <DialogActions sx={{ p: 2 }}>
        <Button onClick={onClose} color="inherit">
          Close
        </Button>
      </DialogActions>
    </Dialog>
  );
};
